import React, { useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useLongPress } from 'react-use';
import cn from 'classnames';
import elementResizeDetectorMaker from 'element-resize-detector';
import moment from 'moment';

import { deleteAllAppointmentsV1 } from 'actions/scheduler/deleteAllAppointments.action';
import fetchEventsBySalon from 'actions/scheduler/fetchEventsBySalon.action';
import fetchProceduresBySalon from 'actions/scheduler/fetchProceduresBySalon.action';
import getProcedureLog from 'actions/scheduler/getProcedureLog.action';
import undoProcedure from 'actions/scheduler/undoProcedure.action';
import updateProcedure from 'actions/scheduler/updateProcedure';
import { feSidebarOpen } from 'actions/ui.actions';
import SalonFlag from 'components/core/SalonFlag.component';
import DndDraggable from 'components/dnd/DndDraggable.component';
import CurrencySign from 'components/price/CurrencySign.component';
import FLAG from 'config/flag.singleton';
import { COUNTRY_CURRENCY } from 'constants/currency.const';
import DELAY from 'constants/delay.const';
import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import SP from 'constants/ui/sidePanel.const';
import { withTranslation } from 'providers/GILT.provider';
import { selectBookingIsUnedited } from 'selectors/booking.selectors';
import { selectCurrentSalonCountryCode, selectCurrentSalonFlags, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectSidebarAppointmentId, selectSidebarOpen } from 'selectors/sidebar.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { formatDate } from 'utils/converters/time/intl.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatHourAndMinuteFull, formatSegmentDay, formatSegmentWeek } from 'utils/date/format.util';
import elementClosest from 'utils/dom/elementClosest.util';
import elementContains from 'utils/dom/elementContains.util';
import isTouchDevice from 'utils/dom/isTouchDevice.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import deRef from 'utils/objects/deRef.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isNotValidId } from 'utils/predicates/fields.util';
import dnd from 'utils/react/dnd.util';

import checkOverLapping from 'booking/components/common/checkOverLapping';
import { priceForStylist } from 'booking/overrides';
import ShowTags from 'clientProfile/components/BasicProfile/ShowTags';
import {
  clearNoshowChargeInfo,
  clearRequestDepositSuccess,
  createIfAllowed,
  deletePreference,
  emptyAppointmentStatuses,
  fetchAppointmentDetails,
  fetchClientLabels,
  fetchDeposit,
  hideInactiveStylists,
  markAppointmentEdit,
  pickCurrentStylist,
  reorderStylists,
  reorderStylistsThenSave,
  requestDeposit,
  revertProcedureLog,
  savePreference,
  saveStylistOrder,
  setBookedByButton,
  setCreation,
  setCurrentDay,
  setDragFrom,
  setDropTime,
  setModal,
  setView,
  showAllStylist,
  updateNewBookingDate,
} from 'scheduler/actions';

import CN from './ScheduleProcedure.module.scss';
const L = logger('ScheduleProcedure');

const USE_SPINNER_INDICATOR = false;
const USE_MOBILE_INDICATOR = false;
const USE_SPAM_LOG = fromAnyToBool(FLAG.loggerSpam);
const DRAG_HANDLER = 6;

const SMALLEST_TRANSITION_DURATION = '0.001s'; // cannot be 0, but make it super tiny

const ERD = elementResizeDetectorMaker({
  strategy: 'scroll', //  <- For ultra performance.
});

const StatusSection = ({ appointment, deposit, client, notes, status }) => {
  const countryCode = useSelector(selectCurrentSalonCountryCode);

  const statusArrived = ['arrived'].includes(status);
  const statusConfirm = ['confirmed', 'paid', 'finished'].includes(status);
  const statusCanceled = ['no_show', 'canceled'].includes(status);
  const statusIconCancelled = ['note', 'unconfirmed', 'online_unconfirmed', 'not_arrived', 'finished', 'no_show', 'error', 'canceled'].includes(status);
  const statusIconConfirmed = ['unconfirmed', 'confirmed'].includes(status);

  const hasDepositIcon = appointment && deposit === 'deposited';
  const statusDR = (appointment && deposit === 'invited') || (appointment && deposit === 'reminded');
  const statusNote = client && notes?.length > 0;
  const statusNew = true === client?.new_client;

  const depositIconClassVariant = COUNTRY_CURRENCY[countryCode];
  const depositIconClass = `${depositIconClassVariant}Status`;

  return (
    <section data-bem="StatusSection" data-tag="ScheduleProcedure__appointment-statuses" className="appointment-statuses">
      <span className={cn({ item: true, [depositIconClass]: hasDepositIcon })} />
      <span className={cn({ item: true, statusDR })} />
      <span className={cn({ item: true, 'ico-confirm': statusIconConfirmed, statusConfirm })} />
      <span className={cn({ item: true, 'ico-canceled': statusIconCancelled, statusCanceled })} />
      <span className={cn({ item: true, 'ico-arrived': statusArrived, statusArrived })} />
      <span className={cn({ item: true, statusNote })} />
      <span className={cn({ item: true, statusNew })} />
    </section>
  );
};

const TouchyButton = React.forwardRef((props, ref) => {
  const { onClick, disableLongPress, ...extra } = props;

  const handleClick = useCallback((ev) => call('TouchyButton.onClick', onClick, ev, { isLongPress: false }), [onClick]);

  const handleLongPress = useCallback((ev) => call('TouchyButton.onLongPress', onClick, ev, { isLongPress: true }), [onClick]);

  // has onMouseDown, onMouseLeave, onMouseUp, onTouchEnd, onTouchStart
  // NOTE: must not prevent the default click, so it can be handled as well
  const longPressAttributes = useLongPress(handleLongPress, { delay: DELAY.split, isPreventDefault: false });

  const passDownProps = { ...extra, ...(disableLongPress ? {} : longPressAttributes) };
  return <div {...passDownProps} data-bem="TouchyButton" onClick={handleClick} ref={ref}></div>;
});

class ScheduleProcedure extends React.Component {
  static defaultProps = {
    editing: false,
    isBeingDragged: false,
    overlapStatus: { procedureOverlap: 'FREE', depth: 0 },
    visibilityWhenManyColumns: '',
  };

  constructor(props) {
    super(props);
    this.cardRef = React.createRef();
    this.wrapperRef = React.createRef();
    this.state = {
      status: 'unconfirmed',
      show15minLabels: true,
      showServiceName: true,
      clientTags: [],
      originalH: 0,
      activeAppointmentId: null,
    };
  }

  componentDidMount() {
    this.updateStatus(this.props);
    document.addEventListener('click', this.handleClickOutside);

    if (this.cardRef && this.cardRef.current) {
      ERD.listenTo(this.cardRef.current, this.checkCardDimensions);
    }
  }

  // componentWillReceiveProps
  UNSAFE_componentWillReceiveProps = (newProps) => {
    this.updateStatus(newProps);
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isSidebarOpen && !this.props.isSidebarOpen) {
      this.setState({ activeAppointmentId: null });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  getStyle = (style, snapshot, backgroundColor, height, handle) => {
    const { originalH: heightFromState } = this.state;
    const { procedure } = this.props;

    if (handle && style?.transform) {
      const transformTuple = asArray(style.transform?.split?.(', '));

      const procedureWrapperElement = document.querySelector(`.is-${procedure?.id} .cal-entry-wrap`);
      const oldHeight = fromAnyToInteger(procedureWrapperElement?.style?.height) || 0;

      if (!heightFromState) this.setState({ originalH: oldHeight });

      if (procedureWrapperElement) {
        const heightChange = fromAnyToInteger(transformTuple[1]) || 0;
        const newHeight = (heightFromState ? heightFromState : oldHeight) + heightChange;
        procedureWrapperElement.style.height = `${newHeight}px`;
      }
    }

    const baseStyle = {
      ...style,
      borderBottomColor: snapshot.isDragging ? 'transparent' : backgroundColor,
      top: `${height - DRAG_HANDLER}px`,
    };

    // if the notification exists, the element was already dragged and dropped
    if (document.querySelector('.move-notification')) return baseStyle;
    if (snapshot.isDropAnimating) return { ...baseStyle, transitionDuration: SMALLEST_TRANSITION_DURATION };
    if (snapshot.isDragging) return { ...baseStyle, visibility: 'hidden', backgroundColor: 'transparent' };

    return baseStyle;
  };

  checkCardDimensions = () => {
    const duration = this.props.procedure?.duration;
    const offsetWidth = this.cardRef?.current?.offsetWidth;

    if (moment.duration(duration).asMinutes() <= 15) this.setState({ show15minLabels: offsetWidth >= 156 });
    this.setState({ showServiceName: offsetWidth >= 240 });
  };

  handleClick = (event) => {
    const { procedure, history, salonId, dayView } = this.props;

    const stylistId = procedure?.stylist?.id;
    const appointmentId = procedure?.appointment?.id || 0;
    const bgn = moment(procedure?.start_time);

    event.stopPropagation();
    this.closeModal();

    const path =
      !dayView && history.location.pathname.includes('/week/')
        ? `/salon/${salonId}/week/${formatSegmentWeek(bgn)}/${stylistId}/appointment/edit/${appointmentId}/load`
        : `/salon/${salonId}/day/${formatSegmentDay(bgn)}/appointment/edit/${appointmentId}/load`;

    history.push(path);
  };

  updateStatus = (props) => {
    const appointment = props.procedure.appointment;
    const status = appointment?.status ? appointment?.status : 'unconfirmed';
    const clientTags = asArray(appointment?.client?.client_tags);

    this.setState({ status, clientTags });
  };

  handleStatusChange = (status, action = '', amount = 0, depositData, isCancelling) => {
    const { procedure, updateProcedureStatus } = this.props;

    this.setState({ status });

    if (status !== 'not_arrived' && procedure?.appointment && procedure?.service) {
      updateProcedureStatus([procedure], status, action, amount, depositData, isCancelling);
    }
  };

  handleClickOutside = (event) => {
    const { target } = asObject(event);

    const SELECTOR = '[data-dialog-body]';
    const ancestorElement = elementClosest({ ancestor: SELECTOR, descendant: target });
    const hasAncestor = fromAnyToBool(ancestorElement);

    // this is a new check to help fix issues like
    // SEE: https://getslick.atlassian.net/browse/GS-10962
    if (hasAncestor) {
      if (USE_SPAM_LOG) L.debug('handleClickOutside()', 'Abort close due to ancestorElement:', ancestorElement);
      return;
    }

    const dialogBodyElement = document.querySelector(SELECTOR);
    const isInside = elementContains({ ancestor: dialogBodyElement, descendant: target });

    // this is a new check to help fix issues like
    // SEE: https://getslick.atlassian.net/browse/GS-10963
    if (isInside) {
      if (USE_SPAM_LOG) L.debug('handleClickOutside()', 'Abort close due to dialogBodyElement:', dialogBodyElement);
      return;
    }

    const { procedure, currentActiveProcedure: activeId } = this.props;
    const { editing: isEditing, id: procedureId } = asObject(procedure);
    const wrapper = deRef(this.wrapperRef);

    // this is the old check
    const isOutside = wrapper && !wrapper.contains(target);
    const isSameId = activeId === procedureId;

    if (!isOutside || !isEditing || !isSameId) {
      if (USE_SPAM_LOG) {
        L.debug('handleClickOutside()', 'Abort close due to', { isOutside, isEditing, isSameId });
      }
      return;
    }

    const portalElement = document.querySelector('.ReactModalPortal');
    const noShowElement = document.querySelector('.dialog-container.no-show');

    const isCountZero = asObject(portalElement).childElementCount === 0;
    const isSmsComposeAndSendOpen = fromAnyToBool(portalElement && isCountZero && !noShowElement);

    if (isSmsComposeAndSendOpen) {
      if (USE_SPAM_LOG) {
        L.debug('handleClickOutside()', 'Abort close due to isSmsComposeAndSendOpen:', isSmsComposeAndSendOpen);
      }
      return;
    }

    const flags = { isOutside, isEditing, isSameId, isCountZero, isSmsComposeAndSendOpen };
    const elements = { wrapper, target, dialogBodyElement, portalElement, noShowElement, ancestorElement };

    if (USE_SPAM_LOG) {
      L.debug('handleClickOutside()', 'Closing modal due to', flags, elements);
    }
  };

  // FIXME: replace with Array.from
  toArray = (obj) => {
    const array = [];
    // iterate backwards ensuring that length is an UInt32
    for (let i = 0; i < obj.length; i += 1) {
      array[i] = obj.item(i);
    }
    return array;
  };

  renderTime = (options) => {
    try {
      const { t } = this.props;
      // FIXME: extract the stuff from this block into a new component

      if (options?.appointmentId) {
        if ('canceled' === options?.status) {
          return <div data-bem="ScheduleProcedure__render-time--canceled--with-id">{t('scheduler.scheduleProcedure.cancelled')}</div>;
        }
        if ('finished' === options?.status) {
          return <div data-bem="ScheduleProcedure__render-time--finished--with-id">{t('scheduler.scheduleProcedure.checkout')}</div>;
        }
        if ('no_show' === options?.status) {
          return <div data-bem="ScheduleProcedure__render-time--noshow--with-id">{t('scheduler.scheduleProcedure.noShow')}</div>;
        }
      }

      const { procedureBgn, duration, standardPrice, procedurePrice } = options ?? {};

      if (duration <= 15) {
        return (
          <div data-bem="ScheduleProcedure__render-time--less-than-15">
            {procedureBgn.format('h:mm a')}
            {standardPrice !== procedurePrice && (
              <>
                <CurrencySign />
                {procedurePrice} <CurrencySign />
                {standardPrice}
              </>
            )}
          </div>
        );
      }

      const { procedureEnd } = options ?? {};

      return (
        <div data-bem="ScheduleProcedure__render-time--more-than-15" className="edited-price">
          <div>
            {procedureBgn.format('h:mm')} - {procedureEnd.format('h:mm a')}{' '}
          </div>
          <div className={fromAnyToString(this.props.visibilityWhenManyColumns)}>
            {standardPrice === procedurePrice ? null : (
              <div>
                {S.dot} {S.space}
                <span className="standard-price">
                  <CurrencySign />
                  {standardPrice}
                </span>
                <span className="new-price">
                  <CurrencySign />
                  {procedurePrice}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    } catch (e) {
      L.warn('renderTime()', 'options:', options, 'error:', e);
      return <div data-bem="ScheduleProcedure__render-time--error" />;
    }
  };

  render() {
    const { show15minLabels, clientTags: clientTagsFromState, showServiceName, status } = this.state;
    const {
      editing: editingFromProps,
      allowModal,
      currentActiveProcedure,
      procedure,
      overlapStatus,
      hoveredCell,
      categoriesAndServices,
      stylists,
      isBeingDragged,
      isDragDisabledIndicator,
      draggingFromDeletedColumMessage,
      feSidebarOpen,
      isBookingUnedited,
      t,
    } = this.props;

    const { depth: overlapDepth, procedureOverlap } = overlapStatus ?? {};
    const { id: procedureId, service, editing: isEditing, appointment, start_time: start, unedited } = procedure ?? {};
    const { name: serviceName, category } = service ?? {};
    const { id: appointmentId, status: appointmentStatus, client, notes, deposit } = appointment ?? {};
    const { id: clientId, status: clientStatus, user_name: userName, new_client: newClient, patch_test_date: patchTestDate } = client ?? {};

    const isBlockHidden = fromAnyToBool(isEditing && isNotValidId(procedureId) && isBookingUnedited);
    if (isBlockHidden) {
      const info = { isEditing, procedureId, isBookingUnedited, start, unedited };
      L.debug('render()', 'isBlockHidden:', isBlockHidden, info, procedure);
    }

    const isNeedsPayment = appointmentStatus === 'needs_payment';
    const isPaid = status === 'paid';
    const isNoShow = status === 'no_show';
    const isModalOpen = fromAnyToBool(allowModal && procedure && currentActiveProcedure === procedureId && isEditing && !editingFromProps);

    let border = 'none';
    const alreadyDraggedEl = document.querySelector('.move-notification');

    const backgroundColor = isPaid ? '#9b9b9b' : (service && fromAnyToTrimmed(asObject(category).color)) || '#9b9b9b';
    const textDecoration = isPaid || isNoShow ? 'line-through' : void 1;

    let overlapBackgroundColor = backgroundColor;
    const el = document.querySelector(`.is-${procedureId}`) || document.querySelector('.is--1000');

    if (el && el.children[0].children.length > 1) {
      // when dragging, the children[0] is the time, it only has one child
      if (el && this.toArray(el.children[0].children[1].classList).includes('cal-active')) {
        if (isEditing || procedureId === -1000) {
          if (checkOverLapping(el).overlap) {
            overlapBackgroundColor = 'transparent';
            border = '5px solid red';
          }
        }
      }
    }

    // Need to add the toggle class 'cal-active' / 'cal-inactive' at the same level as 'cal-entry-wrap'
    // to set the visual state for entries with a status of 'unconfirmed', 'confirmed', 'online_unconfirmed',
    // 'arrived', 'not_arrived', 'finished', 'paid'.
    //
    // 'cal-active' is for new or currently editing (?) entries.
    //
    // 'cal-inactive' is for other entries.
    //
    // NOTE!! No change to 'no_show', 'canceled', 'time-off' and 'error' entries.
    // Thought they didn't require any differentiation as they are not as important as customer service entries.

    const isStatusArrived = ['arrived'].includes(status);
    const isStatusArrivedOrNot = ['arrived', 'not_arrived'].includes(status);
    const isStatusDone = ['finished', 'paid'].includes(status);
    const isStatusError = ['error'].includes(status);
    const isStatusInitial = ['unconfirmed', 'confirmed', 'online_unconfirmed'].includes(status);
    const isStatusNoShowOrError = ['no_show', 'error'].includes(status);
    const isStatusNoShowOrHidden = ['no_show', 'canceled'].includes(status);

    const nameClasses = cn({
      'cal-name': true,
      'cal-state-a': isStatusInitial,
      'cal-state-b': isStatusArrived,
      'cal-state-c': isStatusDone,
      'cal-state-d': ['no_show', 'canceled', 'not_arrived'].includes(status),
      'cal-state-f': isStatusError,
      'cal-txt-b': ['unconfirmed', 'confirmed', 'online_unconfirmed', 'arrived', 'not_arrived', 'finished', 'paid', 'canceled'].includes(status),
      'cal-txt-w': isStatusNoShowOrError,
      'display-on-next-line': !showServiceName,
      'narrow-name': asObject(notes).length > 0 || fromAnyToBool(newClient) || ['invited', 'deposited', 'reminded'].includes(deposit) || ['confirmed', 'arrived', 'paid', 'canceled'].includes(status),
    });

    const bottomLineClasses = cn({
      'cal-botline': true,
      'cal-state-bg-a': isStatusInitial,
      'cal-state-bg-b': isStatusArrivedOrNot,
      'cal-state-bg-c': isStatusDone,
      'cal-state-bg-d': isStatusNoShowOrHidden,
      'cal-state-bg-f': isStatusError,
    });

    const timeClasses = cn({
      'cal-time 3': true,
      'cal-txt-w': isStatusNoShowOrError,
    });

    const notesClasses = cn({
      'cal-notes': true,
      'cal-txt-w': isStatusNoShowOrError,
    });

    const duration = moment.duration(procedure.duration).asMinutes();
    const showServicePopout = duration <= 30;
    const height = (25 * duration) / 15 - 6;

    const procedureBgn = moment(procedure.start_time);
    const procedureEnd = moment(procedure.start_time).add(duration, 'minutes');

    const displayBgn = formatHourAndMinuteFull(procedureBgn);
    const displayEnd = formatHourAndMinuteFull(procedureEnd);

    const wrapClasses = cn({
      'cal-container': true, // if it is not handle normally depending on the duration < 15
      'is-15-min': duration <= 15 || procedureOverlap === 'PARTIALLY_OVERLAP_15_MINUTES', // if it is partially overlap, use special styling
      'and-overlapped': procedureOverlap === 'PARTIALLY_OVERLAP_15_MINUTES',
    });

    const editingState = isEditing || editingFromProps;

    let standardPrice = procedure.price;
    if (service && service.id && procedure.stylist) {
      standardPrice = priceForStylist({ id: service.id, price: procedure.price }, procedure.stylist.id, categoriesAndServices, stylists);
    }

    const cardHeight = `${height}px`; // need to change line height and move notes
    const isClientDeleted = clientStatus === 'deleted';

    const handleEntryClick = () => {
      if (this.state.activeAppointmentId !== appointmentId) {
        this.setState({ activeAppointmentId: appointmentId });
      }
      feSidebarOpen({ panel: SP.appointmentDefault, appointmentId });
    };

    const isActiveCard = this.props.appointmentId === appointmentId;
    const hasAppointment = fromAnyToBool(appointment);

    return (
      <div
        data-bem="ScheduleProcedure"
        data-appointment-id={appointmentId}
        data-procedure-id={procedureId}
        data-pw-id={`ScheduleProcedure:${appointmentId}/${procedureId}`}
        className={cn({
          [CN.component]: true,
          [CN.hidden]: isBlockHidden,
          [`is-${procedureId}`]: true,
          'class-opacity': appointment && appointmentStatus === AP_STATUS.needsPayment,
        })}
      >
        {isDragDisabledIndicator && USE_SPINNER_INDICATOR && <div className={CN.indicator} />}
        <div data-bem="ScheduleProcedure__content" ref={this.wrapperRef} className={wrapClasses}>
          {draggingFromDeletedColumMessage && <div className={CN.draggingMessage}>{t('scheduler.scheduleProcedure.draggingMsg')}</div>}
          <div data-bem="ScheduleProcedure__tooltip-card" className="card-tooltip" style={{ backgroundColor }}>
            <div className="tooltiptext">
              <span>
                {displayBgn} - {displayEnd}
                {S.space}
                {fromAnyToString(asObject(hoveredCell).time)}
              </span>
              <span>
                <strong>{fromAnyToString(userName)}</strong>
              </span>
              <span> {fromAnyToString(serviceName)}</span>
              <span> {fromAnyToString(appointment && notes)}</span>
            </div>
          </div>
          {showServicePopout && (
            <div data-bem="ScheduleProcedure__service" className="cal-service-over">
              <div
                className={cn({
                  'cal-topline': true,
                  'cal-state-bg-a': isStatusInitial,
                  'cal-state-bg-b ico-arrived': isStatusArrivedOrNot,
                  'cal-state-bg-c ': isStatusDone,
                  'cal-state-bg-d': isStatusNoShowOrHidden,
                  'cal-state-bg-f': isStatusError,
                })}
                style={{ backgroundColor }}
              />
              <div className="cal-service cal-txt-w">
                <div className="cal-service-icon cal-state-w" />
                {serviceName}
              </div>
              <div
                className={cn({
                  'cal-botline': true,
                  'cal-state-bg-a': isStatusInitial,
                  'cal-state-bg-b ico-arrived': isStatusArrivedOrNot,
                  'cal-state-bg-c ': isStatusDone,
                  'cal-state-bg-d': isStatusNoShowOrHidden,
                  'cal-state-bg-f': isStatusError,
                })}
                style={{ backgroundColor }}
              />
            </div>
          )}

          <TouchyButton
            disableLongPress
            ref={this.wrapperRef}
            className={cn({
              'cal-entry-wrap disable-selection': true,
              'current-card': isModalOpen,
              'cal-active': editingState,
              'cal-inactive': !editingState,
              'box-shadowed': overlapStatus && overlapDepth !== 0,

              // NOTE: workaround w/ highlight for both flows
              // the new sidebar and the old modal to work together

              highlight: isActiveCard || (isBeingDragged && !alreadyDraggedEl), // in old css
              [CN.highlight]: isActiveCard || (isBeingDragged && !alreadyDraggedEl),
            })}
            style={{
              backgroundColor: overlapBackgroundColor,
              height: cardHeight,
              border,
            }}
            onClick={handleEntryClick}
            role="button"
            tabIndex={0}
          >
            <div
              data-bem="ScheduleProcedure__appointment"
              data-appointment-id={String(appointmentId)}
              data-appointment-status={String(appointmentStatus)}
              data-procedure-id={String(procedureId)}
              data-is-paid={isPaid}
              data-is-no-show={isNoShow}
              data-client-id={String(clientId)}
              data-client-status={String(clientStatus)}
              data-client-name={String(userName)}
              className={cn({
                'cal-entry': true, // Comment out next line to stop default background state of entries from being white.
                'cal-state-bg-f': isStatusError,
              })}
              style={{ height: `${height}px` }}
            >
              <div className="cal-client" id={`appointment_${appointmentId || 0}`}>
                {isPaid || isNoShow ? (
                  <div data-bem="ScheduleProcedure__paid-or-noshow" />
                ) : (
                  <StatusSection appointment={appointment} deposit={deposit} client={client} notes={notes} status={status} />
                )}
                <div className="cal-card-booking-wrapper">
                  {duration <= 15 ? (
                    show15minLabels && (
                      <div data-bem="ScheduleProcedure__calendar-time--max-15" className={timeClasses}>
                        {this.renderTime({
                          appointmentId,
                          procedureBgn,
                          procedureEnd,
                          duration,
                          status,
                          standardPrice,
                          procedurePrice: procedure.price,
                        })}
                      </div>
                    )
                  ) : (
                    <div data-bem="ScheduleProcedure__calendar-time--min-15" className={`exam-class ${timeClasses}`}>
                      {this.renderTime({
                        appointmentId,
                        procedureBgn,
                        procedureEnd,
                        duration,
                        status,
                        standardPrice,
                        procedurePrice: procedure.price,
                      })}
                    </div>
                  )}
                  <div className={nameClasses}>
                    <div
                      className={cn({
                        'client-in-card': true,
                        [CN.lineCross]: appointmentStatus === AP_STATUS.canceled,
                      })}
                    >
                      {clientId && userName && !isClientDeleted ? (
                        <>
                          {hasAppointment && appointmentStatus === AP_STATUS.needsPayment ? (
                            <div />
                          ) : (
                            <div className="client-line">
                              <span className="client-name" style={{ textDecoration }}>
                                {userName}
                              </span>
                              {duration > 15 && isFullArray(clientTagsFromState) ? (
                                <SalonFlag name={SF.clientTags}>
                                  <SalonFlag.IfEnabled>{fromAnyToBool(clientTagsFromState) && <ShowTags hideName tagList={clientTagsFromState} />}</SalonFlag.IfEnabled>
                                </SalonFlag>
                              ) : null}
                            </div>
                          )}
                        </>
                      ) : fromAnyToBool(userName) ? (
                        <span data-bem="ScheduleProcedure__username-description" style={{ textDecoration }}>
                          {userName}
                        </span>
                      ) : (
                        !isClientDeleted && (
                          <span data-bem="ScheduleProcedure__walkin-description" style={{ textDecoration }}>
                            {t('scheduler.scheduleProcedure.walkIn')}
                          </span>
                        )
                      )}
                      {isClientDeleted && (
                        <span data-bem="ScheduleProcedure__deleted-description" style={{ textDecoration }}>
                          {t('scheduler.scheduleProcedure.deletedClient')}
                        </span>
                      )}
                    </div>
                    {duration <= 15 ? (
                      show15minLabels && (
                        <div className="service-in-card" style={{ textDecoration }}>
                          {isNeedsPayment ? (
                            <div style={{ fontSize: '9px', lineHeight: '9px' }}>{t('scheduler.scheduleProcedure.onlineBookingInProgress')}</div>
                          ) : (
                            <>{serviceName && <div>{`- ${serviceName}`}</div>}</>
                          )}
                        </div>
                      )
                    ) : (
                      <div className="service-in-card" style={{ textDecoration }}>
                        {isNeedsPayment ? <div>{t('scheduler.scheduleProcedure.onlineBookingInProgress')}</div> : <>{serviceName && <div>{`- ${serviceName}`}</div>}</>}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={cn({
                    'cal-service': true,
                    'cal-txt-w': isStatusNoShowOrError,
                    'display-on-next-line': !showServiceName, // when resize move
                  })}
                >
                  <div className="notes-in-card">
                    {appointment && notes && (
                      <div style={{ textDecoration }}>
                        {duration <= 15 ? (
                          show15minLabels && <div className={notesClasses}>{notes}</div>
                        ) : (
                          <div className={notesClasses}>
                            <div
                              className={cn({
                                'note-pre-line': true,
                                is1Hour: duration === 60,
                              })}
                            >
                              {notes}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="patch-test">
                    {!patchTestDate
                      ? ''
                      : t('scheduler.scheduleProcedure.lastPatchTest', {
                          date: formatDate(patchTestDate, { hideTime: true, hideWeekday: true }),
                        })}
                  </div>
                </div>
              </div>
            </div>
          </TouchyButton>
          <div>
            {isModalOpen && (
              <div
                data-bem="ScheduleProcedure__line"
                className={cn({
                  card: true,
                  'is-editing': editingState,
                  'is-inactive': !editingState,
                })}
              >
                <div className={bottomLineClasses} style={{ borderBottomColor: backgroundColor, backgroundColor, opacity: 1, top: `${height}px` }} />
              </div>
            )}
            {!isModalOpen && !isTouchDevice() && !this.props.isSidebarOpen && (
              <DndDraggable tag="ScheduleProcedure--dnd-source" draggableId={dnd.resizeProcedure({ procedure })} index={0}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    data-bem="ScheduleProcedure--el-source"
                    className={bottomLineClasses}
                    style={{
                      ...this.getStyle(provided.draggableProps.style, snapshot, backgroundColor, height, 'handle'),
                      filter: 'brightness(0.7)',
                    }}
                  >
                    {USE_MOBILE_INDICATOR && !isTouchDevice() && <span className="grabIndicator">=</span>}
                  </div>
                )}
              </DndDraggable>
            )}
            {!isModalOpen && isTouchDevice() && <div data-bem="ScheduleProcedure__touch-device-placeholder" className={bottomLineClasses} style={this.getStyle({}, {}, backgroundColor, height)} />}
          </div>
        </div>
      </div>
    );
  }
}

const ScheduleProcedureHoc = withRouter(
  connect(
    (state) => ({
      appointmentDetails: state.scheduler.appointmentDetails,
      has_ncob_stripe_account: state.currentSalon.has_ncob_stripe_account,
      salonId: selectCurrentSalonId(state),
      isBookingUnedited: selectBookingIsUnedited(state),
      isSidebarOpen: selectSidebarOpen(state),
      appointmentId: selectSidebarAppointmentId(state),
      salonFlags: selectCurrentSalonFlags(state),
    }),
    mapActionsToDispatchToProps({
      setCreation,
      setModal,
      pickCurrentStylist,
      createIfAllowed,
      updateNewBookingDate,
      fetchProceduresBySalon,
      fetchEventsBySalon,
      deleteAllAppointments: deleteAllAppointmentsV1,
      updateProcedure,
      reorderStylists,
      saveStylistOrder,
      reorderStylistsThenSave,
      hideInactiveStylists,
      showAllStylist,
      setView,
      setCurrentDay,
      fetchAppointmentDetails,
      fetchClientLabels,
      fetchDeposit,
      savePreference,
      getProcedureLog,
      revertProcedureLog,
      deletePreference,
      markAppointmentEdit,
      setBookedByButton,
      setDropTime,
      setDragFrom,
      undoProcedure,
      emptyAppointmentStatuses,
      clearNoshowChargeInfo,
      requestDeposit,
      clearRequestDepositSuccess,
      feSidebarOpen,
    }),
  )(withTranslation()(ScheduleProcedure)),
);

export default ScheduleProcedureHoc;
